import { Icon } from "@ogt/ui";
import { renderToStaticMarkup } from "react-dom/server";

type IconOrChar = string;

export interface SvgMarkerIconProps {
  topIconName?: IconOrChar;
  badgeIconName?: IconOrChar;
  height?: number;
  width?: number;
  showIcon?: boolean;
  color?: string;

  fillColor?: string;
  strokeColor?: string;
  iconColor?: string;
}

function renderIconOrText(
  nameOrChar: IconOrChar | undefined,
  boxSize: number,
  fill = "#fff"
) {
  if (!nameOrChar) return "";

  const isSingleChar = nameOrChar.length === 1;
  const fontSize = boxSize * 0.75;

  if (isSingleChar) {
    return `<text
      x="${boxSize / 2}"
      y="${boxSize / 2}"
      font-family="Arial, sans-serif"
      font-size="${fontSize}"
      fill="${fill}"
      text-anchor="middle"
      dominant-baseline="central"
      font-weight="bold"
    >${nameOrChar.toUpperCase()}</text>`;
  }

  try {
    const markup = renderToStaticMarkup(
      <Icon name={nameOrChar as any} size={boxSize} color={fill} />
    );
    return markup;
  } catch {
    return `<text
      x="${boxSize / 2}"
      y="${boxSize / 2}"
      font-family="Arial, sans-serif"
      font-size="${fontSize}"
      fill="${fill}"
      text-anchor="middle"
      dominant-baseline="central"
      font-weight="bold"
    >${nameOrChar}</text>`;
  }
}
export interface SvgMarkerIconProps {
  topIconName?: IconOrChar;
  badgeIconName?: IconOrChar;
  height?: number;
  width?: number;
  showIcon?: boolean;
  color?: string;

  fillColor?: string;
  strokeColor?: string;
  strokeWidth?: number;
  iconColor?: string;
}

export function SvgMarkerIcon({
  topIconName,
  badgeIconName,
  height = 48,
  width,
  fillColor,
  strokeColor,
  strokeWidth = 2,
  iconColor = "#fff",
  showIcon = true,
  color = "#E10E1C",
}: SvgMarkerIconProps): google.maps.Icon {
  const BASE_W = 40;
  const BASE_H = 48;

  const MARKER_CENTER_X = 19.2861;
  const HEAD_CENTER_Y = 19.2;

  const aspect = BASE_W / BASE_H;

  const outH = height;
  const outW = width ?? Math.round(outH * aspect);

  const TOP_ICON_BOX = BASE_H * 0.3;
  const BADGE_ICON_BOX = TOP_ICON_BOX * 0.8;

  const TOP_ICON_SCALE = 1.5;
  const BADGE_ICON_SCALE = 1.3;

  const topBoxSize = TOP_ICON_BOX * TOP_ICON_SCALE;
  const badgeBoxSize = BADGE_ICON_BOX * BADGE_ICON_SCALE;

  const topIconSvg =
    showIcon && topIconName
      ? `<g transform="translate(${MARKER_CENTER_X}, ${HEAD_CENTER_Y})
                     translate(-${topBoxSize / 2}, -${topBoxSize / 2})">
           ${renderIconOrText(topIconName, topBoxSize, iconColor)}
         </g>`
      : "";

  const TEXT_Y_VB = HEAD_CENTER_Y + 0.2;

  const badgeIconSvg = badgeIconName
    ? `<g transform="translate(${MARKER_CENTER_X}, ${TEXT_Y_VB})
                     translate(-${badgeBoxSize / 2}, -${badgeBoxSize / 2})">
         ${renderIconOrText(badgeIconName, badgeBoxSize, "#fff")}
       </g>`
    : "";

  const resolvedFillColor = fillColor ?? color;
  const resolvedStrokeColor = strokeColor ?? color;

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg"
         width="${outW}" height="${outH}"
         viewBox="0 0 ${BASE_W} ${BASE_H}" fill="none">
      <path
        d="M19.2861 2C23.8733 2.00011 28.2708 3.81445 31.5117 7.04102C34.7524 10.2674 36.5713 14.6413 36.5713 19.2002C36.5713 24.5146 33.5716 30.187 29.7451 35.1924C26.0092 40.0792 21.7219 44.0247 19.5068 45.9346C19.4413 45.9756 19.3656 45.9999 19.2861 46C19.2062 46 19.1293 45.976 19.0635 45.9346C16.8482 44.0245 12.5618 40.0787 8.82617 35.1924C4.99967 30.1871 2 24.5146 2 19.2002C2.00001 14.6413 3.81892 10.2674 7.05957 7.04102C10.3006 3.81436 14.6988 2 19.2861 2Z"
        fill="${resolvedFillColor}"
        stroke="${resolvedStrokeColor}"
        stroke-width="${strokeWidth}"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      ${topIconSvg}
      ${badgeIconSvg}
    </svg>`;

  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(outW, outH),
    anchor: new google.maps.Point((MARKER_CENTER_X / BASE_W) * outW, outH),
  };
}
